import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Box, Typography, Button, Stack, CircularProgress,
  Tabs, Tab, useTheme, Checkbox, FormControlLabel,
  TextField, MenuItem
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { SaveRounded, StorageRounded, RefreshRounded, Lock, LockOpenRounded, WindowRounded, DashboardCustomizeRounded, FileDownloadOutlined, AnalyticsRounded, BuildRounded, Grid3x3Rounded, OpenWithRounded } from '@mui/icons-material'
import { apiGet, apiPost, apiPut } from '../generic/Api_Functions'
import AlertUI from '../generic/AlertUI'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { useSmartInterval } from '../generic/hooks/useSmartInterval'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import CheckDialog from '../generic/CheckDialog'
import createDialogTitle from '../generic/utilities/dialogUtil'
import { PENDING_COLOR, styles } from './warehouseStorageStyles'
import ResourcePanel from './ResourcePanel'
import StorageShelf from './StorageShelf'
import StorageStatusBar from './StorageStatusBar'
import CellDetailPopover from './CellDetailPopover'
import StorageTraceabilityContent from './StorageTraceabilityContent'
import { storageFillOutcome } from './storageFillOutcome'

/**
 * WarehouseStorageManager  —  Gestor de Posiciones de Almacén
 *
 * Visual, touch-friendly interface for managing resource placement in
 * warehouse storage positions. Designed to replace Storage.jsx + StoragePosition.jsx
 * for day-to-day warehouse operations (those screens remain for administration).
 *
 * ── Architecture ─────────────────────────────────────────────────────────────
 *
 *  ┌─────────────────────────────────────────────────────────────────┐
 *  │  WarehouseStorageManager  (coordinator — state + callbacks)     │
 *  │  ┌─────────────┐  ┌──────────────────────────────────────────┐  │
 *  │  │ResourcePanel│  │ Right panel                              │  │
 *  │  │             │  │  ┌──────── Tabs (one per storage) ──────┐│  │
 *  │  │ Available   │  │  │ StorageShelf × N                     ││  │
 *  │  │ resources   │  │  │  └─ StoragePositionCell × M          ││  │
 *  │  │ (paginated  │  │  └──────────────────────────────────────┘│  │
 *  │  │ by type)    │  │  StorageStatusBar (fixed floating pill)  │  │
 *  │  └─────────────┘  └──────────────────────────────────────────┘  │
 *  │  CellDetailPopover  (portal, opens on occupied cell tap)        │
 *  └─────────────────────────────────────────────────────────────────┘
 *
 * ── State model ──────────────────────────────────────────────────────────────
 *
 *  positionsByStorage   { [storageId]: position[] }
 *    Lazily loaded per tab. Each position: { id, shelve, position, depth,
 *    resources[], topResource, fixtureReferences[], commandId, storageIsInOut }
 *    resources[] order from backend: [topResource (Part/Tool/RM), Fixture (base)]
 *
 *  pendingChanges       { [storageId]: { [posId]: change } }
 *    Pending changes are accumulated in memory. A single "Save All" flushes them.
 *    change.action values:
 *      'set'     – place a resource (resource field = ResourceDto)
 *      'remove'  – clear entire position (all resources removed on save)
 *      'unmount' – remove only the top resource; Fixture stays
 *    Move creates a linked pair: source gets 'remove' (linkedResourceId),
 *    target gets 'set' (linkedFromPositionId + linkedFromStorageId).
 *    Undo of either half cancels both via handleCancelPendingChange.
 *
 * ── Backend endpoints used ────────────────────────────────────────────────────
 *
 *  GET  storages                              → list of storages for tabs
 *  GET  shelveStoragePositionsManager/{id}    → all positions for one storage
 *  GET  resourcesAvailableForStorage?excludeFixturesAssignedToResources=true
 *                                             → resources not in any position; with param=true
 *                                               also excludes Fixtures mounted under a Part/Tool/RM
 *  POST storageRemoveResourcesByReference     → batch remove (Save All removes)
 *  POST storageFillPositionsByReference       → batch place  (Save All sets)
 *  GET  storagesBasicData/inOut               → in/out storage list (Move to In/Out)
 *  POST commandMove/request/fixture/{id}      → request fixture move to in/out
 *  PUT  commandMove/cancel/{id}               → cancel in/out move
 *  GET  storage/isBooked/{id}                 → check if storage can be unlocked
 *  PUT  unlockStorage/{id}                    → unlock a storage
 *  POST shelveStoragePositions/emptyAll/{bool}→ empty all positions (global)
 *  POST shelveStoragePositions/fillAll        → fill all positions (global)
 *  GET  storageTraceability/pagination/{id}   → traceability history (modal)
 *
 * ── Auto-refresh ─────────────────────────────────────────────────────────────
 *  Positions for the active tab refresh every 3 s (useSmartInterval) when there
 *  are no pending changes. This keeps the grid in sync with robot/automatic moves.
 */

const typeOf = (r) => r?.typeKey || r?.type?.name || ''

export default function StorageManager ({ initialStorageId: initialStorageIdProp } = {}) {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const theme = useTheme()
  const accent = theme.palette.primary.main
  const location = useLocation()

  const [storages, setStorages] = useState([])
  const [selectedStorageId, setSelectedStorageId] = useState('')

  // Per-storage state keyed by storageId
  const [positionsByStorage, setPositionsByStorage] = useState({})
  const [loadingByStorage, setLoadingByStorage] = useState({})
  const [expandedShelvesByStorage, setExpandedShelvesByStorage] = useState({})

  // pendingChanges[storageId][posId] = { action, resource }
  const [pendingChanges, setPendingChanges] = useState({})

  const [resourcesRefreshKey, setResourcesRefreshKey] = useState(0)
  const [selectedResource, setSelectedResource] = useState(false)
  const [movingResource, setMovingResource] = useState(false)
  const [moveMode, setMoveMode] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null)
  const [popoverPosition, setPopoverPosition] = useState(null)
  const [saving, setSaving] = useState(false)

  // Fixture-use setting: if true, non-Fixture resources require a Fixture base
  const [fixtureUse, setFixtureUse] = useState(false)
  // Pending no-fixture dialog state
  const [noFixtureDialog, setNoFixtureDialog] = useState(false)
  const [noFixtureTarget, setNoFixtureTarget] = useState(false) // { pos, resource }
  const [panelFixtures, setPanelFixtures] = useState([])
  const [availableFixtures, setAvailableFixtures] = useState([])
  const [selectedFixture, setSelectedFixture] = useState(false)

  // Ref to the right panel for measuring its center (used for the floating status bar)
  const rightPanelRef = useRef(null)
  // Tracks which storageId was in-flight when the interval fired, so the
  // response is stored under the correct key even if the user switched tabs.
  const intervalFetchIdRef = useRef(null)
  const [statusBarCenter, setStatusBarCenter] = useState('50vw')

  useEffect(() => {
    const el = rightPanelRef.current
    if (!el) return
    const update = () => {
      const { left, width } = el.getBoundingClientRect()
      setStatusBarCenter(left + width / 2 + 'px')
    }
    update()
    const ro = new window.ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Derived state for the currently selected storage
  const currentPositions = useMemo(() =>
    positionsByStorage[selectedStorageId] || [],
  [positionsByStorage, selectedStorageId])

  const currentLoading = loadingByStorage[selectedStorageId] || false
  const currentExpandedShelves = expandedShelvesByStorage[selectedStorageId] || new Set()
  const currentPendingChanges = useMemo(() =>
    pendingChanges[selectedStorageId] || {},
  [pendingChanges, selectedStorageId])

  useEffect(() => {
    apiGet('settings/FIXTURE_USE')
      .then(data => setFixtureUse(data?.value === 'YES'))
      .catch(() => {})
  }, [])

  const reloadStorages = useCallback(() => {
    apiGet('storages')
      .then(data => {
        const list = Array.isArray(data) ? data : (data.content || [])
        setStorages(list)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    reloadStorages()
  }, [reloadStorages])

  const loadPositionsForStorage = useCallback(async (storageId) => {
    setLoadingByStorage(prev => ({ ...prev, [storageId]: true }))
    try {
      const content = await apiGet('shelveStoragePositionsManager/' + storageId) || []
      setPositionsByStorage(prev => ({ ...prev, [storageId]: content }))
      setExpandedShelvesByStorage(prev => ({
        ...prev,
        [storageId]: new Set(content.map(p => p.shelve))
      }))
    } catch (e) {
      showAlert({ title: t_('Error loading positions'), severity: 'error', message: e.message })
    } finally {
      setLoadingByStorage(prev => ({ ...prev, [storageId]: false }))
    }
  }, [showAlert, t_])

  // Lazy-load positions when a tab is first selected
  useEffect(() => {
    if (!selectedStorageId) return
    if (positionsByStorage[selectedStorageId] === undefined && !loadingByStorage[selectedStorageId]) {
      loadPositionsForStorage(selectedStorageId)
    }
  }, [selectedStorageId, positionsByStorage, loadingByStorage, loadPositionsForStorage])

  // Auto-select first tab when storages load; honour initialStorageId from prop or navigation state
  useEffect(() => {
    if (storages.length === 0 || selectedStorageId) return
    const requestedId = initialStorageIdProp ?? location.state?.initialStorageId
    const match = requestedId && storages.find(s => s.id === Number(requestedId))
    setSelectedStorageId(match ? match.id : storages[0].id)
  }, [storages, selectedStorageId, initialStorageIdProp, location.state])

  // Eager-load positions for ALL storages on first load so global stats are complete
  useEffect(() => {
    if (storages.length === 0) return
    storages.forEach(storage => {
      if (positionsByStorage[storage.id] === undefined && !loadingByStorage[storage.id]) {
        loadPositionsForStorage(storage.id)
      }
    })
  }, [storages]) // eslint-disable-line

  // Auto-refresh the current storage every 3 s when there are no pending changes.
  // intervalFetchIdRef captures the storageId AT REQUEST TIME so the response is
  // always stored under the correct key — even if the user switches tabs mid-flight.
  useSmartInterval(
    async () => {
      if (!selectedStorageId) return
      if (Object.keys(currentPendingChanges).length > 0) return
      if (currentLoading) return
      intervalFetchIdRef.current = selectedStorageId // capture before await
      return await apiGet('shelveStoragePositionsManager/' + selectedStorageId).catch(() => {})
    },
    3000,
    (data) => {
      const fetchedForId = intervalFetchIdRef.current
      if (data && fetchedForId) {
        setPositionsByStorage(prev => ({ ...prev, [fetchedForId]: data }))
      }
    }
  )

  const shelveMap = useMemo(() => {
    const map = {}
    for (const pos of currentPositions) {
      if (!map[pos.shelve]) map[pos.shelve] = {}
      if (!map[pos.shelve][pos.position]) map[pos.shelve][pos.position] = {}
      map[pos.shelve][pos.position][pos.depth] = pos
    }
    return map
  }, [currentPositions])

  const shelveDimensions = useMemo(() => {
    const dims = {}
    for (const pos of currentPositions) {
      if (!dims[pos.shelve]) dims[pos.shelve] = { rows: new Set(), cols: new Set() }
      dims[pos.shelve].rows.add(pos.position)
      dims[pos.shelve].cols.add(pos.depth)
    }
    return dims
  }, [currentPositions])

  const shelveNumbers = useMemo(() =>
    Object.keys(shelveMap).map(Number).sort((a, b) => a - b),
  [shelveMap])

  // Stats aggregated across ALL storages
  const stats = useMemo(() => {
    let total = 0
    let occupied = 0
    let pending = 0
    for (const [sid, positions] of Object.entries(positionsByStorage)) {
      const changes = pendingChanges[sid] || {}
      total += positions.length
      occupied += positions.filter(p => {
        const change = changes[p.id]
        if (change) return change.action === 'set'
        return p.resources && p.resources.length > 0
      }).length
      pending += Object.keys(changes).length
    }
    return { total, occupied, free: total - occupied, pending }
  }, [positionsByStorage, pendingChanges])

  // Stats for the currently selected storage tab only
  const currentStorage = useMemo(() =>
    storages.find(s => s.id === selectedStorageId),
  [storages, selectedStorageId])

  const currentStorageName = currentStorage?.name || ''

  // If the storage is tool-only, restrict the resource panel to Tools
  const storageTypeFilter = currentStorage?.toolStorage ? 'Tool' : false

  const currentStorageStats = useMemo(() => {
    const changes = pendingChanges[selectedStorageId] || {}
    const total = currentPositions.length
    const occupied = currentPositions.filter(p => {
      const change = changes[p.id]
      if (change) return change.action === 'set'
      return p.resources && p.resources.length > 0
    }).length
    return { total, occupied, free: total - occupied }
  }, [currentPositions, pendingChanges, selectedStorageId])

  // IDs of resources already placed in any pending change (set action)
  const pendingResourceIds = useMemo(() => {
    const ids = new Set()
    for (const changes of Object.values(pendingChanges)) {
      for (const change of Object.values(changes)) {
        if (change.action === 'set' && change.resource?.id) {
          ids.add(change.resource.id)
        }
        if (change.action === 'setWithFixture') {
          if (change.resource?.id) ids.add(change.resource.id)
          if (change.fixture?.id) ids.add(change.fixture.id)
        }
      }
    }
    return ids
  }, [pendingChanges])

  const getCellEffectiveResources = useCallback((pos) => {
    const pending = currentPendingChanges[pos.id]
    if (pending) {
      if (pending.action === 'remove') return []
      if (pending.action === 'set') {
        // Also include any Fixture already saved in this position so the popover shows the full stack
        const savedFixture = (pos.resources || []).find(r => (r.typeKey || r.type?.name || '') === 'Fixture')
        if (savedFixture && typeOf(pending.resource) !== 'Fixture') {
          return [pending.resource, savedFixture]
        }
        // Resource has a fixture pre-mounted — show it as the base layer in the display
        if (!savedFixture && typeOf(pending.resource) !== 'Fixture' && pending.resource.fixture) {
          return [pending.resource, { ...pending.resource.fixture, typeKey: 'Fixture' }]
        }
        return [pending.resource]
      }
      if (pending.action === 'unmount') return (pos.resources || []).slice(1) // keep Fixture, remove top
      if (pending.action === 'setWithFixture') return [pending.resource, pending.fixture] // resource on top, fixture as base
    }
    return pos.resources || []
  }, [currentPendingChanges])

  /**
   * Returns the compatibility state of a position for the currently selected/moving resource.
   *   'compatible'   – empty position that accepts this resource's fixture
   *   'incompatible' – empty position that does NOT accept this resource's fixture
   *   'occupied'     – position has resources or a pending set (no compatibility indicator)
   *   null           – no check needed (fixtureUse=false, no resource selected, resource is a Fixture)
   */
  const getCompatibilityForPos = useCallback((pos, resource) => {
    if (!fixtureUse || !resource) return null

    const effectiveResources = getCellEffectiveResources(pos)
    const isFixtureResource = typeOf(resource) === 'Fixture'

    // Position has exactly one Fixture as base: non-Fixture resources can stack on it (compatible),
    // but placing another Fixture on top is not allowed (occupied).
    if (effectiveResources.length === 1 && typeOf(effectiveResources[0]) === 'Fixture') {
      return isFixtureResource ? 'occupied' : 'compatible'
    }

    if (effectiveResources.length > 0) return 'occupied'

    // Empty position — check fixture reference compatibility
    const refs = pos.fixtureReferences || []

    // A position with no configured fixture types is not a valid target in fixtureUse mode
    if (refs.length === 0) return 'incompatible'

    if (isFixtureResource) {
      const fixtureRefCode = resource.fixtureReference?.code
      // If we cannot determine the Fixture's type, accept any configured position
      if (!fixtureRefCode) return 'compatible'
      return refs.some(ref => ref.code === fixtureRefCode) ? 'compatible' : 'incompatible'
    }

    // Non-Fixture resource: try direct fixtureReference, then fall back to the mounted fixture's type
    const refCode = resource.fixtureReference?.code || resource.fixture?.fixtureReference?.code
    if (!refCode) return 'compatible' // Unknown type → accept any configured position
    return refs.some(ref => ref.code === refCode) ? 'compatible' : 'incompatible'
  }, [fixtureUse, getCellEffectiveResources])

  const handleMoveStart = useCallback((posId, resource) => {
    const sourcePos = (positionsByStorage[selectedStorageId] || []).find(p => p.id === posId)
    const sourceFixture = (sourcePos?.resources || []).find(r => typeOf(r) === 'Fixture') || null
    setMovingResource({ resource, fixture: sourceFixture, fromStorageId: selectedStorageId, fromPositionId: posId })
    setPopoverAnchorEl(null)
    setPopoverPosition(null)
  }, [selectedStorageId, positionsByStorage])

  const handleCancelMove = useCallback(() => setMovingResource(false), [])

  // Clears pending changes for a storage AND cancels any cross-storage move links
  // so reloading one side of a cross-storage move doesn't leave orphan pending changes.
  const clearStoragePendingWithLinks = useCallback((storageId) => {
    setPendingChanges(prev => {
      let updated = { ...prev }
      const changes = updated[storageId] || {}

      for (const change of Object.values(changes)) {
        // Move-target (set): cancel the linked source remove in the other storage
        if (change.action === 'set' && change.linkedFromStorageId && change.linkedFromStorageId !== storageId) {
          const fromChanges = { ...(updated[change.linkedFromStorageId] || {}) }
          delete fromChanges[change.linkedFromPositionId]
          updated = { ...updated, [change.linkedFromStorageId]: fromChanges }
        }
        // Move-source (remove): cancel the linked target set in any other storage
        if (change.action === 'remove' && change.linkedResourceId) {
          for (const [sid, sChanges] of Object.entries(updated)) {
            if (sid === storageId) continue
            const cleaned = { ...sChanges }
            let dirty = false
            for (const [pid, c] of Object.entries(cleaned)) {
              if (c.action === 'set' && c.resource?.id === change.linkedResourceId) {
                delete cleaned[pid]
                dirty = true
              }
            }
            if (dirty) updated = { ...updated, [sid]: cleaned }
          }
        }
      }

      delete updated[storageId]
      return updated
    })
  }, [])

  const handleCellClick = useCallback((event, pos) => {
    // --- Move mode ---
    if (movingResource) {
      if (pos.id === movingResource.fromPositionId && selectedStorageId === movingResource.fromStorageId) {
        setMovingResource(false) // clicked source cell → cancel
        return
      }
      const currentResources = getCellEffectiveResources(pos)
      const pendingForPos = currentPendingChanges[pos.id]
      const canPlace =
        currentResources.length === 0 ||
        (currentResources.length === 1 && typeOf(currentResources[0]) === 'Fixture' && typeOf(movingResource.resource) !== 'Fixture') ||
        pendingForPos?.action === 'set' // can always overwrite a pending (unsaved) placement
      // Block incompatible positions in move mode too
      if (canPlace && getCompatibilityForPos(pos, movingResource.resource) === 'incompatible') {
        const reqCodes = (pos.fixtureReferences || []).map(r => r.code).join(', ') || '—'
        showAlert({ title: t_('Incompatible position'), message: `${t_('Requires fixture')}: ${reqCodes}`, severity: 'warning' })
        return
      }

      if (canPlace) {
        setPendingChanges(prev => {
          const resId = movingResource.resource.id
          let updated = { ...prev }

          // Remove any existing pending set/remove pair for this resource
          // so the same resource can never appear in two positions at once
          for (const [sid, changes] of Object.entries(updated)) {
            const cleaned = { ...changes }
            let dirty = false
            for (const [pid, c] of Object.entries(cleaned)) {
              if (
                (c.action === 'set' && c.resource?.id === resId) ||
                (c.action === 'remove' && c.linkedResourceId === resId)
              ) {
                delete cleaned[pid]
                dirty = true
              }
            }
            if (dirty) updated = { ...updated, [sid]: cleaned }
          }

          // If the target cell already holds a pending move-set (from a different move),
          // overwriting it must also cancel THAT move's linked source remove — otherwise the
          // displaced resource is removed at its source on save but never placed anywhere
          // (silent stock loss). Mirrors the place-mode handling below.
          const existingTarget = (updated[selectedStorageId] || {})[pos.id]
          if (existingTarget?.action === 'set' && existingTarget?.linkedFromPositionId !== undefined) {
            const exFromSid = existingTarget.linkedFromStorageId || selectedStorageId
            const exFrom = { ...(updated[exFromSid] || {}) }
            delete exFrom[existingTarget.linkedFromPositionId]
            updated = { ...updated, [exFromSid]: exFrom }
          }

          const fromStorage = { ...(updated[movingResource.fromStorageId] || {}) }
          fromStorage[movingResource.fromPositionId] = { action: 'remove', linkedResourceId: resId }

          const moveChange = { action: 'set', resource: movingResource.resource, fixture: movingResource.fixture, linkedFromPositionId: movingResource.fromPositionId, linkedFromStorageId: movingResource.fromStorageId }
          if (movingResource.fromStorageId === selectedStorageId) {
            // Same storage: add the set into the same object to avoid key collision
            fromStorage[pos.id] = moveChange
            return { ...updated, [selectedStorageId]: fromStorage }
          }
          const toStorage = { ...(updated[selectedStorageId] || {}) }
          toStorage[pos.id] = moveChange
          return { ...updated, [movingResource.fromStorageId]: fromStorage, [selectedStorageId]: toStorage }
        })
        setMovingResource(false)
      } else {
        showAlert({ title: t_('Cannot place resource here'), message: t_('Cell already occupied or incompatible stacking'), severity: 'warning' })
      }
      return
    }

    // --- Place mode ---
    if (selectedResource) {
      const currentResources = getCellEffectiveResources(pos)
      const pendingForPos = currentPendingChanges[pos.id]
      const canPlace =
        currentResources.length === 0 ||
        (currentResources.length === 1 && typeOf(currentResources[0]) === 'Fixture' && typeOf(selectedResource) !== 'Fixture') ||
        pendingForPos?.action === 'set' // can always overwrite a pending (unsaved) placement

      // If fixture use is required, position is empty, resource is non-Fixture and has no default fixture
      const needsFixture = fixtureUse &&
        typeOf(selectedResource) !== 'Fixture' &&
        currentResources.length === 0 &&
        !selectedResource.fixture // skip if resource already has a fixture pre-assigned
      // Block incompatible positions (fixture mismatch) — shown with red tint in the grid
      if (getCompatibilityForPos(pos, selectedResource) === 'incompatible') {
        const reqCodes = (pos.fixtureReferences || []).map(r => r.code).join(', ') || '—'
        showAlert({
          title: t_('Incompatible position'),
          message: `${t_('Requires fixture')}: ${reqCodes}`,
          severity: 'warning'
        })
        return
      }

      if (needsFixture) {
        setNoFixtureTarget({ pos, resource: selectedResource })
        setSelectedFixture(false)
        const acceptedRefCodes = new Set((pos.fixtureReferences || []).map(r => r.code).filter(Boolean))
        const filtered = panelFixtures
          .filter(f => !pendingResourceIds.has(f.id))
          .filter(f => {
            if (acceptedRefCodes.size === 0) return true
            const refCode = f.fixtureReference?.code
            return !refCode || acceptedRefCodes.has(refCode)
          })
        setAvailableFixtures(filtered)
        setNoFixtureDialog(true)
        return
      }

      if (canPlace) {
        setPendingChanges(prev => {
          let updated = { ...prev }
          const existing = (updated[selectedStorageId] || {})[pos.id]

          // If overwriting a pending set from a move, cancel its linked remove too
          if (existing?.action === 'set' && existing?.linkedFromPositionId !== undefined) {
            const fromSid = existing.linkedFromStorageId || selectedStorageId
            const fromPending = { ...(updated[fromSid] || {}) }
            delete fromPending[existing.linkedFromPositionId]
            updated = { ...updated, [fromSid]: fromPending }
          }

          // If overwriting a pending remove, store it so Undo can restore it
          const newChange = { action: 'set', resource: selectedResource }
          if (existing?.action === 'remove') {
            newChange.previousChange = existing
          }

          return {
            ...updated,
            [selectedStorageId]: {
              ...(updated[selectedStorageId] || {}),
              [pos.id]: newChange
            }
          }
        })
        setSelectedResource(false)
      } else {
        showAlert({ title: t_('Cannot place resource here'), message: t_('Cell already occupied or incompatible stacking'), severity: 'warning' })
      }
      return
    }

    // --- Inspect mode ---
    const effectiveResources = getCellEffectiveResources(pos)
    if (effectiveResources.length > 0 || currentPendingChanges[pos.id]) {
      // Move mode: auto-start move for the top resource without opening the popover
      if (moveMode && effectiveResources.length > 0 && !currentPendingChanges[pos.id]) {
        const topRes = effectiveResources.find(r => typeOf(r) !== 'Fixture') || effectiveResources[0]
        handleMoveStart(pos.id, topRes)
        return
      }
      setPopoverAnchorEl(event.currentTarget)
      setPopoverPosition(pos)
    }
  }, [movingResource, panelFixtures, pendingResourceIds, selectedResource, selectedStorageId, getCellEffectiveResources, currentPendingChanges, moveMode, handleMoveStart, showAlert, t_, fixtureUse, getCompatibilityForPos])

  const handleRemoveFromCell = useCallback((posId) => {
    setPendingChanges(prev => {
      const storagePending = { ...(prev[selectedStorageId] || {}) }
      if (storagePending[posId]?.action === 'set') {
        // Resource was placed as a pending change (not yet saved).
        // Removing it cancels the pending set → net change = 0.
        delete storagePending[posId]
      } else {
        // Resource exists in the backend → mark for removal.
        storagePending[posId] = { action: 'remove' }
      }
      return { ...prev, [selectedStorageId]: storagePending }
    })
    setPopoverAnchorEl(null)
    setPopoverPosition(null)
  }, [selectedStorageId])

  const handleUnmountFromCell = useCallback((posId) => {
    setPendingChanges(prev => ({
      ...prev,
      [selectedStorageId]: {
        ...(prev[selectedStorageId] || {}),
        [posId]: { action: 'unmount' }
      }
    }))
    setPopoverAnchorEl(null)
    setPopoverPosition(null)
  }, [selectedStorageId])

  const handleCancelPendingChange = useCallback((posId) => {
    setPendingChanges(prev => {
      const cancelled = (prev[selectedStorageId] || {})[posId]
      let updated = { ...prev }

      // Cancel the change — if it replaced a previous change (e.g. a pending remove),
      // restore that previous change instead of deleting entirely
      const storagePending = { ...(updated[selectedStorageId] || {}) }
      if (cancelled?.previousChange) {
        storagePending[posId] = cancelled.previousChange
      } else {
        delete storagePending[posId]
      }
      updated = { ...updated, [selectedStorageId]: storagePending }

      // If cancelling a move's remove → also cancel the linked set (same resource)
      if (cancelled?.linkedResourceId) {
        for (const [sid, changes] of Object.entries(updated)) {
          const newChanges = { ...changes }
          let touched = false
          for (const [pid, c] of Object.entries(newChanges)) {
            if (c.action === 'set' && c.resource?.id === cancelled.linkedResourceId) {
              delete newChanges[pid]
              touched = true
            }
          }
          if (touched) updated = { ...updated, [sid]: newChanges }
        }
      }

      // If cancelling a move's set → also cancel the linked remove (same from-position)
      if (cancelled?.linkedFromPositionId !== undefined) {
        const fromSid = cancelled.linkedFromStorageId || selectedStorageId
        const fromPending = { ...(updated[fromSid] || {}) }
        delete fromPending[cancelled.linkedFromPositionId]
        updated = { ...updated, [fromSid]: fromPending }
      }

      return updated
    })
    setPopoverAnchorEl(null)
    setPopoverPosition(null)
  }, [selectedStorageId])

  const saveAllChanges = useCallback(async () => {
    const hasChanges = Object.values(pendingChanges).some(c => Object.keys(c).length > 0)
    if (!hasChanges) {
      showAlert({ title: t_('No pending changes'), severity: 'info' })
      return
    }
    setSaving(true)
    const affectedStorages = Object.keys(pendingChanges).filter(sid => Object.keys(pendingChanges[sid]).length > 0)

    // Resources from resourcesAvailableForStorage may have typeKey but type: null.
    // The backend endpoint requires type to be present for deserialization.
    const withType = (res) => {
      if (!res || res.type) return res
      const tk = res.typeKey || ''
      const name = tk === 'RawMaterial' ? 'Raw Material' : tk
      return { ...res, type: { name } }
    }

    try {
      const removeItems = []
      const setItems = []

      for (const [storageId, changes] of Object.entries(pendingChanges)) {
        if (Object.keys(changes).length === 0) continue
        const storagePositions = positionsByStorage[storageId] || []
        for (const [posIdStr, change] of Object.entries(changes)) {
          const posId = parseInt(posIdStr)
          const position = storagePositions.find(p => p.id === posId)
          if (!position) continue
          if (change.action === 'setWithFixture') {
            // Place fixture first, then resource on top — backend processes them in order
            const f = { ...change.fixture, typeKey: change.fixture.typeKey || 'Fixture' }
            setItems.push({ positionId: posId, resource: withType(f) })
            setItems.push({ positionId: posId, resource: withType(change.resource) })
          } else if (change.action === 'unmount') {
            // Remove only the top resource (index 0), leaving the base Fixture
            const topRes = position.resources?.[0]
            if (topRes) removeItems.push({ positionId: posId, resource: topRes })
          } else if (change.action === 'remove') {
            // Remove all resources from the position
            for (const res of (position.resources || [])) {
              removeItems.push({ positionId: posId, resource: res })
            }
          } else {
            // Set — if the position already has backend resources, remove only the
            // non-Fixture ones so that a base Fixture is preserved when stacking.
            const existing = position.resources || []
            const newTypeKey = typeOf(change.resource)
            for (const res of existing) {
              if (typeOf(res) === 'Fixture' && newTypeKey !== 'Fixture') continue
              removeItems.push({ positionId: posId, resource: res })
            }
            // If the resource has an associated fixture (from move or pre-mounted) and
            // the target has no fixture base, place the fixture first.
            const targetHasFixture = existing.some(r => typeOf(r) === 'Fixture')
            // Only send the fixture separately when it comes from a move (change.fixture).
            // When the resource already has a fixture pre-associated in the backend
            // (change.resource.fixture), the backend handles it automatically — sending
            // it again would trigger a "resource already has a Fixture" conflict.
            if (change.fixture && newTypeKey !== 'Fixture' && !targetHasFixture) {
              const fixtureWithKey = { ...change.fixture, typeKey: change.fixture.typeKey || 'Fixture' }
              setItems.push({ positionId: posId, resource: withType(fixtureWithKey) })
            }
            setItems.push({ positionId: posId, resource: withType(change.resource) })
          }
        }
      }

      if (removeItems.length > 0) await apiPost('storageRemoveResourcesByReference', removeItems)
      if (setItems.length > 0) await apiPost('storageFillPositionsByReference', setItems)

      setPendingChanges({})
      showAlert({ title: t_('Changes saved successfully'), severity: 'success' })
    } catch (error) {
      showAlert({ title: t_('Error saving changes'), message: error.message, severity: 'error' })
    } finally {
      // Always resync with the backend, even on partial failure: the removes POST may have
      // already applied before the sets POST failed, so the grid and resource panel must
      // reflect the real backend state. Reloading also makes a retry safe — removeItems are
      // re-derived from fresh position.resources, so already-removed resources are not removed
      // again, and the un-applied sets stay pending to be retried.
      setResourcesRefreshKey(prev => prev + 1)
      await Promise.all(affectedStorages.map(sid => loadPositionsForStorage(sid)))
      setSaving(false)
    }
  }, [pendingChanges, positionsByStorage, loadPositionsForStorage, showAlert, t_])

  const toggleShelf = useCallback((shelveNum) => {
    setExpandedShelvesByStorage(prev => {
      const current = new Set(prev[selectedStorageId] || [])
      if (current.has(shelveNum)) current.delete(shelveNum)
      else current.add(shelveNum)
      return { ...prev, [selectedStorageId]: current }
    })
  }, [selectedStorageId])

  const closePopover = useCallback(() => {
    setPopoverAnchorEl(null)
    setPopoverPosition(null)
  }, [])

  const hasPendingChanges = Object.values(pendingChanges).some(c => Object.keys(c).length > 0)

  // ── Storage lock / unlock ──────────────────────────────────────────
  const [lockStorageId, setLockStorageId] = useState(false)
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false)
  const [showCheckDialog, setShowCheckDialog] = useState(false)

  const handleLockClick = useCallback(async (storageId, e) => {
    e.stopPropagation()
    const bookable = await apiGet('storage/isBooked/' + storageId).catch(() => false)
    if (bookable) {
      setLockStorageId(storageId)
      setUnlockDialogOpen(true)
    } else {
      showAlert({ title: t_('Error'), message: t_('Storage is locked by the system, please wait'), severity: 'error' })
    }
  }, [showAlert, t_])

  const handleUnlockStorage = useCallback(async () => {
    await apiPut('unlockStorage/' + lockStorageId)
      .then(() => showAlert({ title: t_('Storage successfully unlocked.'), severity: 'success' }))
      .catch(error => showAlert({ title: t_('An error occurred while unlocking storage.'), message: error.message, severity: 'error' }))
    setUnlockDialogOpen(false)
    reloadStorages()
  }, [lockStorageId, showAlert, t_, reloadStorages])

  // ── Empty all / Fill all ───────────────────────────────────────────
  const [emptyAllDialogOpen, setEmptyAllDialogOpen] = useState(false)
  const [fillAllDialogOpen, setFillAllDialogOpen] = useState(false)
  const [emptyOnlyFinishedParts, setEmptyOnlyFinishedParts] = useState(false)

  // ── Move to In/Out ─────────────────────────────────────────────────
  const [inOutDialogOpen, setInOutDialogOpen] = useState(false)
  const [inOutStorageList, setInOutStorageList] = useState([])
  const [inOutTargetDeviceId, setInOutTargetDeviceId] = useState('')
  const [inOutPositionId, setInOutPositionId] = useState(false)

  const handleMoveToInOut = useCallback(async (positionId) => {
    const data = await apiGet('storagesBasicData/inOut').catch(() => [])
    if (!data || data.length === 0) {
      showAlert({ title: t_('There is not a storage set as an In/Out.'), severity: 'warning' })
      return
    }
    setInOutStorageList(data)
    setInOutTargetDeviceId(data[0].deviceId)
    setInOutPositionId(positionId)
    setInOutDialogOpen(true)
    setPopoverAnchorEl(null)
    setPopoverPosition(null)
  }, [showAlert, t_])

  const handleConfirmInOut = useCallback(async () => {
    const storagePositions = positionsByStorage[selectedStorageId] || []
    const position = storagePositions.find(p => p.id === inOutPositionId)
    if (!position) return
    // The In/Out endpoint moves a Fixture. Resolve the actual Fixture in the position
    // instead of assuming it is the last resource — a position may hold only a Part/Tool/RM
    // with no fixture base (e.g. fixtureUse off), and sending a non-Fixture id to
    // commandMove/request/fixture would move the wrong entity.
    const fixtureId = (position.resources || []).find(r => typeOf(r) === 'Fixture')?.id
    // Defensive: the popover only offers this action for fixture-bearing positions, so
    // reaching here without a Fixture should not happen — abort quietly rather than POST
    // a non-Fixture id to the fixture-move endpoint.
    if (!fixtureId) {
      setInOutDialogOpen(false)
      return
    }
    const storageData = await apiGet('storages/' + selectedStorageId).catch(() => ({}))
    const fromDeviceId = storageData.deviceId
    const body = [
      { deviceId: fromDeviceId, positionId: inOutPositionId },
      { deviceId: inOutTargetDeviceId, positionId: false }
    ]
    await apiPost('commandMove/request/fixture/' + fixtureId, body)
      .then(() => {
        showAlert({ title: t_('Fixture request out set correctly.'), severity: 'success' })
        loadPositionsForStorage(selectedStorageId)
      })
      .catch(error => showAlert({ title: t_('An error occurred while setting fixture as request out.'), message: error.message, severity: 'error' }))
    setInOutDialogOpen(false)
  }, [inOutPositionId, inOutTargetDeviceId, positionsByStorage, selectedStorageId, loadPositionsForStorage, showAlert, t_])

  const handleCancelInOut = useCallback(async (commandId) => {
    await apiPut('commandMove/cancel/' + commandId)
      .then(() => {
        showAlert({ title: t_('Fixture request out canceled correctly.'), severity: 'success' })
        loadPositionsForStorage(selectedStorageId)
      })
      .catch(error => showAlert({ title: t_('An error occurred while canceling fixture as request out.'), message: error.message, severity: 'error' }))
    setPopoverAnchorEl(null)
    setPopoverPosition(null)
  }, [selectedStorageId, loadPositionsForStorage, showAlert, t_])

  // ── Traceability modal ────────────────────────────────────────────
  const [traceabilityOpen, setTraceabilityOpen] = useState(false)
  const [traceabilityStorageId, setTraceabilityStorageId] = useState(false)
  const [traceabilityStorageName, setTraceabilityStorageName] = useState('')

  const openTraceability = useCallback((storageId, storageName) => {
    setTraceabilityStorageId(storageId)
    setTraceabilityStorageName(storageName)
    setTraceabilityOpen(true)
  }, [])

  const handleEmptyAll = useCallback(() => {
    apiPost('shelveStoragePositions/emptyAll/' + emptyOnlyFinishedParts)
      .then(() => {
        showAlert({ title: t_('Success'), message: t_('Storage has been succesfully emptied'), severity: 'success' })
        setPendingChanges({})
        Object.keys(positionsByStorage).forEach(sid => loadPositionsForStorage(sid))
      })
      .catch(error => showAlert({ title: t_('Error emptying storage'), message: error.message, severity: 'error' }))
    setEmptyAllDialogOpen(false)
  }, [emptyOnlyFinishedParts, showAlert, t_, positionsByStorage, loadPositionsForStorage])

  const handleFillAll = useCallback(() => {
    apiPost('shelveStoragePositions/fillAll')
      .then((response) => {
        // The backend no longer fills all or nothing, so a 200 is not a full storage any more.
        // `storageFillOutcome` is what decides the wording -- see the reasoning there.
        const outcome = storageFillOutcome(response)
        showAlert({ title: t_(outcome.titleKey), message: t_(outcome.messageKey, outcome.values), severity: outcome.severity })
        Object.keys(positionsByStorage).forEach(sid => loadPositionsForStorage(sid))
      })
      .catch(error => showAlert({ title: t_('Error filling storage'), message: error.message, severity: 'error' }))
    setFillAllDialogOpen(false)
  }, [showAlert, t_, positionsByStorage, loadPositionsForStorage])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1.5, minHeight: 0 }}>
      {AlertElement}

      {/* Traceability modal */}
      <SmartDialog
        setOpen={traceabilityOpen}
        title={createDialogTitle({ title: traceabilityStorageName, icon: AnalyticsRounded })}
        cancel={false}
        accept={false}
        close
        closeCallback={() => setTraceabilityOpen(false)}
        paperWidth='90vw'
        scroll='paper'
        growContainerHeight
        renderComponent={
          traceabilityOpen && traceabilityStorageId
            ? <StorageTraceabilityContent storageId={traceabilityStorageId} storageName={traceabilityStorageName} />
            : false
        }
      />

      {/* No-fixture dialog — shown when placing a non-Fixture in a position with no base fixture */}
      <SmartDialog
        setOpen={noFixtureDialog}
        title={createDialogTitle({ title: t_('Fixture required'), icon: BuildRounded })}
        message={t_('This position has no fixture. Select one to place first.')}
        accept={!!selectedFixture}
        cancel
        close={false}
        acceptCallback={() => {
          if (!noFixtureTarget || !selectedFixture) return
          const { pos, resource } = noFixtureTarget
          setPendingChanges(prev => ({
            ...prev,
            [selectedStorageId]: {
              ...(prev[selectedStorageId] || {}),
              [pos.id]: { action: 'setWithFixture', fixture: selectedFixture, resource }
            }
          }))
          setSelectedResource(false)
          setNoFixtureDialog(false)
          setNoFixtureTarget(false)
          setSelectedFixture(false)
        }}
        cancelCallback={() => { setNoFixtureDialog(false); setNoFixtureTarget(false); setSelectedFixture(false) }}
        renderComponent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1, maxHeight: 300, overflow: 'auto' }}>
            {availableFixtures.length === 0
              ? <Typography sx={{ color: '#888', textAlign: 'center', py: 2 }}>{t_('No fixtures available')}</Typography>
              : availableFixtures.map(f => (
                <Box
                  key={f.id}
                  onClick={() => setSelectedFixture(selectedFixture?.id === f.id ? false : f)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    bgcolor: selectedFixture?.id === f.id ? 'rgba(255,152,0,0.1)' : 'rgba(255,255,255,0.03)',
                    border: '1px solid',
                    borderColor: selectedFixture?.id === f.id ? 'rgba(255,152,0,0.4)' : 'rgba(255,255,255,0.06)',
                    '&:hover': { bgcolor: 'rgba(255,152,0,0.06)' }
                  }}
                >
                  <Grid3x3Rounded sx={{ fontSize: 18, color: '#ff9800' }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.82rem', color: '#ddd' }}>{f.name}</Typography>
                    {f.code && <Typography sx={{ fontSize: '0.68rem', color: '#666' }}>{f.code}</Typography>}
                  </Box>
                </Box>
              ))}
          </Box>
        }
      />

      {/* Empty All dialog */}
      <SmartDialog
        setOpen={emptyAllDialogOpen}
        message={t_('Are you sure you want to empty all the parts from this storage?')}
        title={createDialogTitle({ title: t_('Empty all positions?'), icon: WindowRounded })}
        accept={false}
        cancelCallback={() => setEmptyAllDialogOpen(false)}
        close={false}
        renderCustomButtons={
          <Button style={{ borderColor: theme => theme.palette.primary.main }} onClick={handleEmptyAll} variant='outlined'>
            <Typography color='primary'>{t_('EMPTY')}</Typography>
            <WindowRounded color='primary' sx={{ fontSize: '1.4em', ml: '0.3rem' }} />
          </Button>
        }
        renderComponent={
          <FormControlLabel
            label={t_('Empty only finished parts')}
            control={
              <Checkbox
                checked={emptyOnlyFinishedParts}
                onChange={e => setEmptyOnlyFinishedParts(e.target.checked)}
              />
            }
          />
        }
      />

      {/* Move to In/Out dialog */}
      <SmartDialog
        setOpen={inOutDialogOpen}
        message={t_('Are you sure you want to empty current position to the in/Out module?')}
        title={createDialogTitle({ title: t_('Empty to In/Out?'), icon: FileDownloadOutlined })}
        accept
        cancel
        close={false}
        acceptCallback={handleConfirmInOut}
        cancelCallback={() => setInOutDialogOpen(false)}
        renderComponent={inOutStorageList.length > 1
          ? (
            <>
              <Typography sx={{ mb: 1 }}>{t_('Please, select the storage where the fixture will be emptied')}</Typography>
              <TextField
                select
                label={t_('Storage')}
                value={inOutTargetDeviceId}
                onChange={e => setInOutTargetDeviceId(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 200, mt: 1 }}
              >
                {inOutStorageList.map(s => (
                  <MenuItem key={s.id} value={s.deviceId}>{s.name}</MenuItem>
                ))}
              </TextField>
            </>
            )
          : false}
      />

      {/* Fill All dialog */}
      <SmartDialog
        setOpen={fillAllDialogOpen}
        message={t_('Are you sure you want to Fill all Storages?')}
        title={createDialogTitle({ title: t_('Fill All Storages?'), icon: DashboardCustomizeRounded })}
        accept={false}
        cancelCallback={() => setFillAllDialogOpen(false)}
        close={false}
        renderCustomButtons={
          <Button sx={{ borderColor: theme => theme.palette.primary.main }} onClick={handleFillAll} variant='outlined'>
            <Typography color='primary'>{t_('FILL ALL')}</Typography>
            <DashboardCustomizeRounded color='primary' sx={{ fontSize: '1.4em', ml: '0.3rem' }} />
          </Button>
        }
      />

      {/* Unlock storage dialog */}
      <SmartDialog
        setOpen={unlockDialogOpen}
        message={t_('Are you sure you want to unlock this storage?')}
        title={createDialogTitle({ title: t_('Unlock storage?'), icon: LockOpenRounded })}
        accept
        cancel
        close={false}
        acceptCallback={() => {
          // CheckDialog decides which steps apply based on the cell state and the REQUIRE_PASSWORD setting.
          setShowCheckDialog(true)
          setUnlockDialogOpen(false)
        }}
        cancelCallback={() => setUnlockDialogOpen(false)}
      />

      <CheckDialog
        open={showCheckDialog}
        onClose={() => setShowCheckDialog(false)}
        onAccept={handleUnlockStorage}
      />

      {/* Header — sticky so it stays visible while scrolling through shelves */}
      <Box sx={{
        ...styles.headerBar,
        py: 1.5,
        px: 3,
        mx: -3,
        position: 'sticky',
        top: 0,
        zIndex: 20,
        bgcolor: 'background.default',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.06)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        alignItems: 'center'
      }}
      >
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <StorageRounded sx={{ color: accent, fontSize: 30 }} />
          <Typography variant='h5' sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
            {t_('Storage Manager')}
          </Typography>
        </Stack>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Button
            variant='outlined'
            startIcon={<DashboardCustomizeRounded />}
            onClick={() => setFillAllDialogOpen(true)}
            sx={{ ...styles.accentBtn, minHeight: 44, fontSize: '0.85rem' }}
          >
            {t_('Fill All')}
          </Button>
          <Button
            variant='outlined'
            startIcon={<WindowRounded />}
            onClick={() => setEmptyAllDialogOpen(true)}
            sx={{ color: '#ff5252', borderColor: 'rgba(255,82,82,0.35)', minHeight: 44, px: 2, fontSize: '0.85rem', '&:hover': { borderColor: '#ff5252', bgcolor: 'rgba(255,82,82,0.05)' } }}
          >
            {t_('Empty All')}
          </Button>
          <Button
            variant={moveMode ? 'contained' : 'outlined'}
            startIcon={<OpenWithRounded />}
            onClick={() => setMoveMode(prev => !prev)}
            sx={moveMode
              ? { minHeight: 44, px: 2, fontSize: '0.85rem', fontWeight: 700, bgcolor: accent, color: '#000', '&:hover': { bgcolor: alpha(accent, 0.85) } }
              : { ...styles.accentBtn, minHeight: 44, fontSize: '0.85rem' }}
          >
            {t_('Move Mode')}
          </Button>
          <Button
            variant='contained'
            color='success'
            startIcon={saving ? <CircularProgress size={16} color='inherit' /> : <SaveRounded />}
            onClick={saveAllChanges}
            disabled={saving || !hasPendingChanges}
            sx={{
              minHeight: 44,
              px: 2,
              fontWeight: 600,
              fontSize: '0.85rem',
              boxShadow: '0 4px 16px rgba(76,175,80,0.3)',
              '&:not(:disabled):hover': { boxShadow: '0 6px 20px rgba(76,175,80,0.45)' }
            }}
          >
            {t_('Save All')}{stats.pending > 0 ? ` (${stats.pending})` : ''}
          </Button>
        </Stack>
      </Box>

      {/* Main layout */}
      <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
        <ResourcePanel
          pendingResourceIds={pendingResourceIds}
          refreshKey={resourcesRefreshKey}
          selectedResource={selectedResource}
          searchText={searchText}
          onSelectResource={setSelectedResource}
          onSearchChange={setSearchText}
          typeFilter={storageTypeFilter}
          onFixturesLoaded={setPanelFixtures}
        />

        {/* Right panel — ref used to compute the floating status bar center */}
        <Box ref={rightPanelRef} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

          {/* Storage tabs */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            bgcolor: 'rgba(40,40,39,0.5)',
            flexShrink: 0
          }}
          >
            <Tabs
              value={selectedStorageId || false}
              onChange={(_, v) => setSelectedStorageId(v)}
              variant='scrollable'
              scrollButtons='auto'
              sx={{
                flexGrow: 1,
                minHeight: 64,
                '& .MuiTabs-indicator': { backgroundColor: accent },
                '& .MuiTab-root': {
                  color: '#777',
                  minHeight: 64,
                  textTransform: 'none',
                  fontSize: '1rem',
                  px: 3
                },
                '& .Mui-selected': { color: accent, fontWeight: 600 }
              }}
            >
              {storages.map(storage => {
                const tabPendingCount = Object.keys(pendingChanges[storage.id] || {}).length
                return (
                  <Tab
                    key={storage.id}
                    value={storage.id}
                    label={
                      <Stack direction='row' alignItems='center' spacing={0.8}>
                        <span>{storage.name}</span>
                        {storage.deviceBooked
                          ? <Lock sx={{ fontSize: 18, color: 'error.main' }} />
                          : <LockOpenRounded sx={{ fontSize: 18, color: alpha(accent, 0.35) }} />}
                        {tabPendingCount > 0 && (
                          <Box sx={{
                            minWidth: 18,
                            height: 18,
                            px: 0.5,
                            borderRadius: 1,
                            bgcolor: PENDING_COLOR,
                            color: '#000',
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          >
                            {tabPendingCount}
                          </Box>
                        )}
                      </Stack>
                    }
                  />
                )
              })}
            </Tabs>
            {storages.find(s => s.id === selectedStorageId)?.deviceBooked && (
              <Button
                variant='contained'
                color='error'
                startIcon={<Lock />}
                onClick={() => handleLockClick(selectedStorageId, { stopPropagation: () => {} })}
                sx={{ ml: 1, flexShrink: 0, minHeight: 48, px: 2.5, fontWeight: 600, fontSize: '0.88rem' }}
              >
                {t_('Unlock storage?')}
              </Button>
            )}
            <Button
              variant='outlined'
              startIcon={<AnalyticsRounded />}
              onClick={() => {
                const s = storages.find(st => st.id === selectedStorageId)
                openTraceability(selectedStorageId, s?.name || '')
              }}
              disabled={!selectedStorageId}
              sx={{
                ...styles.accentBtn,
                mr: 0,
                ml: 1,
                flexShrink: 0,
                minHeight: 44,
                fontSize: '0.88rem'
              }}
            >
              {t_('Traceability')}
            </Button>
            <Button
              variant='outlined'
              startIcon={currentLoading ? <CircularProgress size={18} color='inherit' /> : <RefreshRounded />}
              onClick={() => {
                clearStoragePendingWithLinks(selectedStorageId)
                loadPositionsForStorage(selectedStorageId)
              }}
              disabled={!selectedStorageId || currentLoading}
              sx={{
                ...styles.accentBtn,
                mx: 1,
                flexShrink: 0,
                minHeight: 44,
                minWidth: 130,
                fontSize: '0.88rem'
              }}
            >
              {t_('Reload')}
            </Button>
          </Box>

          {/* Positions info bar */}
          {selectedStorageId && !currentLoading && currentPositions.length > 0 && (
            <Typography sx={{
              fontSize: '0.95rem',
              color: accent,
              fontWeight: 500,
              px: 1.5,
              py: 0.5,
              flexShrink: 0,
              borderBottom: '1px solid rgba(255,255,255,0.04)'
            }}
            >
              {shelveNumbers.length} {t_('shelves')} · {currentPositions.length} {t_('positions')}
            </Typography>
          )}

          {/* Shelf grid — flexGrow fills remaining height, scrolls within right panel */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', pr: 0.5, pt: 1, pb: 8 }}>
            {!selectedStorageId
              ? (
                <Box sx={styles.emptyState}>
                  <StorageRounded sx={{ fontSize: 72, color: alpha(accent, 0.15) }} />
                  <Typography sx={{ color: '#3a4255', fontSize: '1rem' }}>
                    {t_('Select a storage to view positions')}
                  </Typography>
                </Box>
                )
              : currentLoading
                ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
                    <CircularProgress sx={{ color: accent }} />
                  </Box>
                  )
                : currentPositions.length === 0
                  ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
                      <Typography sx={{ color: '#3a4255' }}>
                        {t_('No positions configured for this storage')}
                      </Typography>
                    </Box>
                    )
                  : shelveNumbers.map(shelveNum => (
                    <StorageShelf
                      key={shelveNum}
                      shelveNum={shelveNum}
                      dims={shelveDimensions[shelveNum]}
                      shelveMap={shelveMap[shelveNum]}
                      positions={currentPositions.filter(p => p.shelve === shelveNum)}
                      pendingChanges={currentPendingChanges}
                      isExpanded={currentExpandedShelves.has(shelveNum)}
                      onToggle={() => toggleShelf(shelveNum)}
                      selectedResource={selectedResource}
                      isSelecting={(selectedResource !== false && selectedResource !== null) || !!movingResource}
                      onCellClick={handleCellClick}
                      getCompatibility={pos => getCompatibilityForPos(pos, selectedResource || movingResource?.resource)}
                    />
                  ))}
          </Box>

          <StorageStatusBar
            stats={stats}
            currentStorageName={currentStorageName}
            currentStorageStats={currentStorageStats}
            selectedResource={selectedResource}
            onCancelSelection={() => setSelectedResource(false)}
            movingResource={movingResource}
            onCancelMove={handleCancelMove}
            panelCenter={statusBarCenter}
          />
        </Box>
      </Box>

      <CellDetailPopover
        anchorEl={popoverAnchorEl}
        position={popoverPosition}
        effectiveResources={popoverPosition ? getCellEffectiveResources(popoverPosition) : []}
        hasPending={popoverPosition ? !!currentPendingChanges[popoverPosition.id] : false}
        onClose={closePopover}
        onRemove={handleRemoveFromCell}
        onUnmount={handleUnmountFromCell}
        onUndo={handleCancelPendingChange}
        onMove={handleMoveStart}
        onMoveToInOut={handleMoveToInOut}
        onCancelInOut={handleCancelInOut}
      />
    </Box>
  )
}
